<template>
  <div class="knowledge-link bg-neutral-50 min-h-screen">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <!-- Hero -->
      <section class="text-center mb-8">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 text-sky-600 text-xs font-medium mb-4"
        >
          <i class="fa fa-link"></i>
          学科知识点体系
        </div>
        <h1 class="text-3xl md:text-5xl font-bold text-neutral-900 mb-4">知识链接</h1>
        <p class="text-base md:text-lg text-neutral-500 max-w-2xl mx-auto">
          按学科把知识点串成体系：章节、知识点、要点与例题，随时补充、随时查阅。
        </p>
      </section>

      <!-- 学段 -->
      <section class="mb-4">
        <div class="flex gap-2">
          <button
            v-for="st in stages"
            :key="st.id"
            @click="switchStage(st.id)"
            class="px-3.5 py-1.5 rounded-full text-xs transition-colors"
            :class="
              stage === st.id
                ? 'bg-neutral-900 text-white'
                : 'bg-white border border-neutral-200 text-neutral-600 hover:border-neutral-400'
            "
          >
            {{ st.label }}
          </button>
        </div>
      </section>

      <!-- 学科 + 操作 -->
      <section class="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div class="flex gap-2 overflow-x-auto pb-1">
          <button
            v-for="s in subjects"
            :key="s"
            @click="switchSubject(s)"
            class="px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors"
            :class="
              subject === s
                ? 'bg-primary text-white font-medium'
                : 'bg-white text-neutral-600 border border-neutral-200 hover:border-primary hover:text-primary'
            "
          >
            {{ s }}
          </button>
        </div>
        <div class="flex items-center gap-2">
          <div class="flex bg-neutral-100 rounded-lg p-0.5">
            <button
              v-for="v in views"
              :key="v.id"
              @click="switchView(v.id)"
              class="px-3 py-1.5 rounded-md text-xs transition-colors"
              :class="view === v.id ? 'bg-white text-neutral-900 shadow-sm font-medium' : 'text-neutral-500'"
            >
              <i class="fa mr-1" :class="v.icon"></i>{{ v.label }}
            </button>
          </div>
          <button
            @click="openCreate(null)"
            class="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90"
          >
            <i class="fa fa-plus mr-1"></i> 新增章节
          </button>
        </div>
      </section>

      <template v-if="view === 'list'">
      <!-- 加载 -->
      <div v-if="loading" class="text-center py-16 text-neutral-400">
        <i class="fa fa-circle-o-notch fa-spin text-2xl"></i>
      </div>

      <!-- 空态 -->
      <div
        v-else-if="!tree.length"
        class="bg-white rounded-xl border border-dashed border-neutral-300 py-16 text-center"
      >
        <i class="fa fa-sitemap text-3xl text-neutral-300 mb-3"></i>
        <p class="text-neutral-500 text-sm">{{ subject }}还没有知识点体系</p>
        <button
          @click="openCreate(null)"
          class="mt-4 px-4 py-2 rounded-lg bg-primary text-white text-sm"
        >
          从第一个章节开始
        </button>
      </div>

      <!-- 知识树 -->
      <div v-else class="space-y-4">
        <div
          v-for="root in tree"
          :key="root.id"
          class="bg-white rounded-xl border border-neutral-200 overflow-hidden"
        >
          <!-- 章节行 -->
          <div
            class="flex items-center gap-3 p-4 cursor-pointer hover:bg-neutral-50"
            @click="toggle(root.id)"
          >
            <i
              class="fa text-neutral-400 text-xs transition-transform"
              :class="expanded[root.id] ? 'fa-chevron-down' : 'fa-chevron-right'"
            ></i>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <p class="font-bold text-neutral-900 truncate">{{ root.title }}</p>
                <span
                  v-if="root.isSystem"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-400 flex-shrink-0"
                  >内置</span
                >
              </div>
              <p class="text-xs text-neutral-400 mt-0.5">
                {{ (root.children || []).length }} 个知识点
              </p>
            </div>
            <div class="flex items-center gap-1.5" @click.stop>
              <button
                class="px-2 py-1 rounded text-xs text-neutral-500 hover:text-primary hover:bg-neutral-100"
                title="添加知识点"
                @click="openCreate(root)"
              >
                <i class="fa fa-plus"></i>
              </button>
              <button
                v-if="canEdit(root)"
                class="px-2 py-1 rounded text-xs text-neutral-500 hover:text-primary hover:bg-neutral-100"
                title="编辑章节"
                @click="openEdit(root)"
              >
                <i class="fa fa-pencil"></i>
              </button>
              <button
                v-if="!root.isSystem"
                class="px-2 py-1 rounded text-xs text-neutral-400 hover:text-red-500 hover:bg-neutral-100"
                title="删除章节"
                @click="remove(root)"
              >
                <i class="fa fa-trash-o"></i>
              </button>
            </div>
          </div>

          <!-- 知识点列表 -->
          <div v-if="expanded[root.id]" class="border-t border-neutral-100">
            <div
              v-if="!(root.children || []).length"
              class="px-4 py-4 text-xs text-neutral-400"
            >
              还没有知识点，
              <button class="text-primary hover:underline" @click="openCreate(root)">
                添加一个
              </button>
            </div>
            <div
              v-for="leaf in root.children"
              :key="leaf.id"
              class="flex items-center gap-3 px-4 py-3 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 group"
            >
              <i class="fa fa-circle text-[5px] text-sky-400 ml-2"></i>
              <div class="flex-1 min-w-0 cursor-pointer" @click="openView(leaf)">
                <p class="text-sm text-neutral-800 truncate">{{ leaf.title }}</p>
                <p
                  v-if="leaf.content"
                  class="text-xs text-neutral-400 truncate mt-0.5"
                >
                  {{ plain(leaf.content) }}
                </p>
              </div>
              <span
                v-if="leaf.isSystem"
                class="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-400 flex-shrink-0"
                >内置</span
              >
              <div
                class="flex items-center gap-1 transition-opacity"
                :class="canEdit(leaf) || !leaf.isSystem ? 'opacity-0 group-hover:opacity-100' : 'opacity-0'"
              >
                <button
                  v-if="canEdit(leaf)"
                  class="px-2 py-1 rounded text-xs text-neutral-500 hover:text-primary"
                  @click="openEdit(leaf)"
                >
                  <i class="fa fa-pencil"></i>
                </button>
                <button
                  v-if="!leaf.isSystem"
                  class="px-2 py-1 rounded text-xs text-neutral-400 hover:text-red-500"
                  @click="remove(leaf)"
                >
                  <i class="fa fa-trash-o"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </template>

      <!-- 知识树 / 知识图谱 -->
      <section
        v-else
        class="bg-white rounded-xl border border-neutral-200 overflow-hidden"
      >
        <div class="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-100 flex-wrap">
          <div class="flex items-center gap-2 text-xs text-neutral-500 flex-wrap">
            <span class="font-bold text-neutral-900 text-sm">
              <i class="fa text-primary mr-1" :class="view === 'net' ? 'fa-share-alt' : 'fa-sitemap'"></i>
              {{ subject }}{{ view === "net" ? "知识图谱" : "知识树" }}
            </span>
            <span>{{ netStats.nodes || 0 }} 个节点</span>
            <span class="text-neutral-300">/</span>
            <span>{{ netStats.relations || 0 }} 条关联</span>
            <span class="text-neutral-400">
              {{ view === "net" ? "关联越多、错题越多的知识点越大越红" : "按章节层级展开，节点越大越红说明越重要" }}
            </span>
            <span
              v-if="canEditSystem"
              class="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100"
            >
              <i class="fa fa-key mr-1"></i>已获授权，可编辑内置知识点
            </span>
          </div>
          <div class="flex items-center gap-2">
            <button
              @click="openCreate(null)"
              class="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
            >
              <i class="fa fa-plus mr-1"></i>新增章节
            </button>
          </div>
        </div>
        <div
          v-if="netError"
          class="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 flex items-center justify-between gap-3"
        >
          <span><i class="fa fa-info-circle mr-1"></i>{{ netError }}</span>
          <button class="text-primary hover:underline shrink-0" @click="loadNet">重试</button>
        </div>
        <div v-if="netLoading" class="py-24 text-center text-neutral-400">
          <i class="fa fa-circle-o-notch fa-spin text-xl"></i>
        </div>
        <KnowledgeNet
          v-else
          :nodes="netNodes"
          :edges="netEdges"
          :selected-id="selectedId"
          :mode="view"
          @select="onSelectNode"
        />
      </section>
    </main>

    <!-- 查看 / 编辑 -->
    <div
      v-if="visible"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4"
    >
      <div class="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold text-neutral-900">
            {{ editing ? (form.id ? "编辑知识点" : "新增") : "知识点详情" }}
          </h2>
          <button class="text-neutral-400 hover:text-neutral-600" @click="close">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- 阅读态 -->
        <div v-if="!editing">
          <p class="text-xs text-neutral-400 mb-1">
            {{ detail.subject }}<span v-if="detail.parentTitle"> · {{ detail.parentTitle }}</span>
          </p>
          <h3 class="text-xl font-bold text-neutral-900 mb-3">{{ detail.title }}</h3>
          <div class="space-y-3">
            <div>
              <p class="text-xs font-medium text-neutral-400 mb-1">具体内容</p>
              <div
                class="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap min-h-[60px]"
              >
                {{ detail.content || "还没有填写具体内容" }}
              </div>
            </div>
            <div class="rounded-lg bg-neutral-50 p-3">
              <p class="text-xs font-medium text-neutral-500 mb-1">
                <i class="fa fa-book mr-1"></i>课标要求
              </p>
              <p class="text-xs text-neutral-700 leading-relaxed whitespace-pre-wrap">
                {{ detail.standard || "未填写" }}
              </p>
            </div>
            <div class="grid sm:grid-cols-2 gap-3">
              <div class="rounded-lg bg-emerald-50/70 p-3">
                <p class="text-xs font-medium text-emerald-700 mb-1">
                  <i class="fa fa-star mr-1"></i>重点
                </p>
                <p class="text-xs text-neutral-700 leading-relaxed whitespace-pre-wrap">
                  {{ detail.keyPoints || "未填写" }}
                </p>
              </div>
              <div class="rounded-lg bg-rose-50/70 p-3">
                <p class="text-xs font-medium text-rose-600 mb-1">
                  <i class="fa fa-exclamation-triangle mr-1"></i>难点
                </p>
                <p class="text-xs text-neutral-700 leading-relaxed whitespace-pre-wrap">
                  {{ detail.difficulties || "未填写" }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="(detail.relatedTitles || []).length" class="mt-5">
            <p class="text-xs text-neutral-400 mb-1.5">
              <i class="fa fa-share-alt mr-1"></i>关联知识点
            </p>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="t in detail.relatedTitles"
                :key="t"
                class="text-xs px-2 py-0.5 rounded bg-sky-50 text-sky-600 border border-sky-100"
              >
                {{ t }}
              </span>
            </div>
          </div>
          <p
            v-if="detail.isSystem && !canEditSystem"
            class="text-[11px] text-neutral-400 mt-4 bg-neutral-50 rounded-lg px-3 py-2"
          >
            <i class="fa fa-lock mr-1"></i>
            这是系统内置知识点，如需修正内容，可联系管理员为你开启「知识体系编辑」权限。
          </p>
          <div class="flex justify-end gap-2 mt-6">
            <button
              class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600"
              @click="close"
            >
              关闭
            </button>
            <button
              class="px-4 py-2 rounded-lg border border-primary/30 text-primary text-sm hover:bg-primary/5"
              @click="openLesson(detail)"
            >
              <i class="fa fa-graduation-cap mr-1"></i>教学诊断
              <span v-if="lessonScores[detail.id]" class="ml-1 text-xs text-neutral-400">
                最近 {{ lessonScores[detail.id] }} 分
              </span>
            </button>
            <button
              v-if="canEdit(detail)"
              class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium"
              @click="startEdit(detail)"
            >
              编辑
            </button>
          </div>
        </div>

        <!-- 编辑态 -->
        <div v-else class="space-y-4">
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">标题 *</label>
            <input
              v-model="form.title"
              maxlength="100"
              placeholder="如：分数乘整数"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="block text-xs font-medium text-neutral-600">
                具体内容（这个知识点包含哪些东西）
              </label>
              <button
                type="button"
                class="text-xs text-primary hover:underline disabled:opacity-50"
                :disabled="drafting"
                @click="generateDraft"
              >
                <i class="fa mr-1" :class="drafting ? 'fa-circle-o-notch fa-spin' : 'fa-magic'"></i>
                {{ drafting ? "生成中..." : "AI 生成初稿" }}
              </button>
            </div>
            <textarea
              v-model="form.content"
              rows="8"
              placeholder="如：声母 23 个（b p m f …）、韵母 24 个（单韵母/复韵母/鼻韵母）…"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-y"
            ></textarea>
            <p class="text-[11px] text-neutral-400 mt-1">
              AI 会同时补全课标要求、重点与难点，生成后可以按自己的教学习惯修改。
            </p>
          </div>
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">课标要求</label>
            <textarea
              v-model="form.standard"
              rows="2"
              placeholder="课程标准对这一知识点的要求…"
              class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-y"
            ></textarea>
          </div>
          <div class="grid sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">重点</label>
              <textarea
                v-model="form.keyPoints"
                rows="3"
                placeholder="本节课最重要的 2-3 点…"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-y"
              ></textarea>
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">难点</label>
              <textarea
                v-model="form.difficulties"
                rows="3"
                placeholder="学生容易卡住、易错的地方…"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-y"
              ></textarea>
            </div>
          </div>
          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">
              关联知识点（可选，用于把知识织成网）
            </label>
            <div
              class="max-h-40 overflow-y-auto border border-neutral-200 rounded-lg p-2 flex flex-wrap gap-1.5"
            >
              <label
                v-for="r in relatedOptions"
                :key="r.id"
                class="flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-pointer transition-colors"
                :class="
                  form.related.includes(r.id)
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-neutral-200 text-neutral-600 hover:border-neutral-300'
                "
              >
                <input type="checkbox" :value="r.id" v-model="form.related" class="hidden" />
                <i
                  class="fa"
                  :class="form.related.includes(r.id) ? 'fa-check-square-o' : 'fa-square-o'"
                ></i>
                {{ r.title }}
                <span v-if="r.isSystem" class="text-[10px] text-neutral-400">内置</span>
              </label>
              <p v-if="!relatedOptions.length" class="text-xs text-neutral-400 px-1 py-1">
                暂无其他知识点可关联
              </p>
            </div>
          </div>
          <p class="text-[11px] text-neutral-400">
            归属：{{ form.subject }}<span v-if="form.parentTitle"> · {{ form.parentTitle }}</span>
          </p>

          <div class="flex justify-end gap-2 pt-2">
            <button
              class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600"
              @click="close"
            >
              取消
            </button>
            <button
              class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50"
              :disabled="saving"
              @click="save"
            >
              {{ saving ? "保存中..." : "保存" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 教学诊断 -->
    <LessonPanel
      v-if="lessonVisible"
      :link="lessonLink"
      @close="lessonVisible = false"
      @score="onLessonScore"
    />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { requireLoginPage, isLogin } from "@/utils/authGuard";
import KnowledgeNet from "@/components/KnowledgeNet.vue";
import LessonPanel from "@/components/LessonPanel.vue";

export default {
  name: "KnowledgeLinkPage",
  components: { KnowledgeNet, LessonPanel },
  data() {
    return {
      loading: false,
      saving: false,
      drafting: false,
      view: "list",
      views: [
        { id: "list", label: "列表", icon: "fa-list-ul" },
        { id: "tree", label: "知识树", icon: "fa-sitemap" },
        { id: "net", label: "知识图谱", icon: "fa-share-alt" },
      ],
      canEditSystem: false,
      netLoading: false,
      netNodes: [],
      netEdges: [],
      netStats: {},
      netError: "",
      selectedId: "",
      lessonVisible: false,
      lessonLink: {},
      lessonScores: {},
      stages: [
        { id: "primary", label: "小学" },
        { id: "junior", label: "初中" },
        { id: "senior", label: "高中" },
      ],
      stage: "primary",
      subjects: ["语文", "数学", "英语", "物理", "化学", "生物", "政治", "历史", "地理", "科学"],
      subject: "语文",
      flat: [],
      expanded: {},

      visible: false,
      editing: false,
      detail: {},
      form: this.emptyForm(),
    };
  },
  computed: {
    tree() {
      const roots = this.flat.filter((x) => !x.parentId);
      const byParent = {};
      this.flat.forEach((x) => {
        if (x.parentId) {
          (byParent[x.parentId] = byParent[x.parentId] || []).push(x);
        }
      });
      return roots.map((r) => ({ ...r, children: byParent[r.id] || [] }));
    },
    /** 可关联的知识点：同科目下除自己与直接子节点之外的节点 */
    relatedOptions() {
      const self = this.form.id;
      const kids = new Set(
        this.flat.filter((x) => x.parentId && x.parentId === self).map((x) => x.id)
      );
      return this.flat.filter((x) => x.id !== self && !kids.has(x.id));
    },
  },
  async mounted() {
    if (!requireLoginPage(this, "知识链接")) return;
    await this.loadSubjects();
    await this.load();
  },
  methods: {
    emptyForm() {
      return {
        id: "",
        subject: "语文",
        parentId: "",
        parentTitle: "",
        title: "",
        content: "",
        standard: "",
        keyPoints: "",
        difficulties: "",
        related: [],
      };
    },

    plain(text) {
      return (text || "").replace(/\s+/g, " ").slice(0, 60);
    },

    /** 学科以后台「学科分类」为准，接口不可用时退回内置列表 */
    async loadSubjects() {
      try {
        const res = await this.$axios({ url: "subinfo/list", method: "GET" });
        const list = (res && res.data) || [];
        const names = (Array.isArray(list) ? list : [])
          .map((x) => (typeof x === "string" ? x : x.title))
          .filter(Boolean);
        if (names.length) {
          this.subjects = names;
          if (!names.includes(this.subject)) {
            this.subject = names.includes("语文") ? "语文" : names[0];
          }
        }
      } catch (e) {
        /* 接口异常时保留默认学科，不影响使用 */
      }
    },

    async load() {
      if (!isLogin()) return;
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "knowledge-link/list",
          method: "GET",
          params: { subject: this.subject, stage: this.stage },
        });
        if (res.code === 200 && res.data) {
          this.flat = res.data.list || [];
          this.canEditSystem = !!res.data.canEditSystem;
          // 默认展开第一个章节
          if (this.flat.length) {
            const firstRoot = this.flat.find((x) => !x.parentId);
            if (firstRoot) this.expanded[firstRoot.id] = true;
          }
        } else {
          ElMessage.error(res.msg || "加载失败");
        }
      } catch (e) {
        ElMessage.error("加载失败，请稍后重试");
      }
      this.loading = false;
    },

    async switchView(v) {
      if (this.view === v) return;
      this.view = v;
      this.selectedId = "";
      if (v !== "list") {
        await this.load();
        await this.loadNet();
      }
    },

    /** 拉图谱接口，返回 null 表示接口不可用 */
    async fetchNet(subject, stage) {
      const params = {};
      if (subject) params.subject = subject;
      if (stage) params.stage = stage;
      const res = await this.$axios({
        url: "knowledge-link/net",
        method: "GET",
        params,
      });
      if (res && res.code === 200 && res.data && typeof res.data === "object") {
        return res.data;
      }
      return null;
    },

    applyNet(data) {
      this.netNodes = data.nodes || [];
      this.netEdges = data.edges || [];
      this.netStats = data.stats || {};
      if (data.canEditSystem !== undefined) {
        this.canEditSystem = !!data.canEditSystem;
      }
    },

    async loadNet() {
      if (!isLogin()) return;
      this.netLoading = true;
      this.netError = "";
      try {
        const data = await this.fetchNet(this.subject, this.stage);
        if (data && (data.nodes || []).length) {
          this.applyNet(data);
        } else {
          // 当前学科/学段筛不出知识点时，放开筛选再取一次，避免图谱一片空白
          const all = await this.fetchNet("", "");
          if (all && (all.nodes || []).length) {
            this.applyNet(all);
            this.netError = `「${this.subject}」${this.stageLabel()}下暂无知识点，已展示全部学科`;
          } else {
            this.buildLocalNet();
            this.netError = this.netNodes.length
              ? "图谱接口没有返回数据，已按列表内容生成"
              : "还没有知识点，先在「列表」里添加章节和知识点";
          }
        }
      } catch (e) {
        console.warn("[knowledge-net] 图谱接口异常，改用列表数据兜底", e);
        this.buildLocalNet();
        this.netError = this.netNodes.length
          ? "图谱接口不可用，已按列表内容生成"
          : "图谱接口不可用，且当前没有知识点数据";
      }
      this.netLoading = false;
    },

    stageLabel() {
      const hit = this.stages.find((s) => s.id === this.stage);
      return hit ? hit.label : "";
    },

    /** 兜底：直接用列表数据在本地组装节点与连线，保证图谱一定画得出来 */
    buildLocalNet() {
      const rows = this.flat || [];
      const ids = new Set(rows.map((x) => x.id));
      const childCount = {};
      const relDegree = {};
      const edges = [];
      const seen = new Set();
      rows.forEach((x) => {
        if (x.parentId && ids.has(x.parentId)) {
          childCount[x.parentId] = (childCount[x.parentId] || 0) + 1;
          edges.push({ source: x.parentId, target: x.id, type: "tree" });
        }
        (x.related || "")
          .split(/[,，、;；\s]+/)
          .map((s) => s.trim())
          .filter((s) => s && s !== x.id && ids.has(s))
          .forEach((rid) => {
            const key = x.id < rid ? x.id + "|" + rid : rid + "|" + x.id;
            if (seen.has(key)) return;
            seen.add(key);
            relDegree[x.id] = (relDegree[x.id] || 0) + 1;
            relDegree[rid] = (relDegree[rid] || 0) + 1;
            edges.push({ source: x.id, target: rid, type: "rel" });
          });
      });

      const scores = {};
      let max = 1;
      rows.forEach((x) => {
        const s = (childCount[x.id] || 0) + (relDegree[x.id] || 0) * 2;
        scores[x.id] = s;
        max = Math.max(max, s);
      });

      this.netNodes = rows.map((x) => ({
        id: x.id,
        title: x.title,
        subject: x.subject,
        stage: x.stage,
        parentId: x.parentId,
        isSystem: !!x.isSystem,
        editable: x.editable !== undefined ? x.editable : !x.isSystem,
        type: childCount[x.id] > 0 || !x.parentId ? "chapter" : "point",
        wrongCount: 0,
        relCount: relDegree[x.id] || 0,
        childCount: childCount[x.id] || 0,
        importance: 25 + Math.round((scores[x.id] / max) * 75),
        mastery: 100,
      }));
      this.netEdges = edges;
      this.netStats = {
        nodes: this.netNodes.length,
        edges: edges.length,
        relations: edges.filter((e) => e.type === "rel").length,
      };
    },

    async onSelectNode(node) {
      this.selectedId = node.id;
      let hit = this.flat.find((x) => x.id === node.id);
      // 图谱接口不含正文，缺失时先补一次列表数据，避免编辑时把内容清空
      if (!hit) {
        await this.load();
        hit = this.flat.find((x) => x.id === node.id);
      }
      this.openView(hit || node);
    },

    canEdit(node) {
      if (!node) return false;
      if (node.editable !== undefined && node.editable !== null) return !!node.editable;
      return !node.isSystem || this.canEditSystem;
    },

    openLesson(node) {
      if (!isLogin()) return;
      this.lessonLink = {
        id: node.id,
        title: node.title,
        subject: node.subject || this.subject,
        stage: node.stage || this.stage,
      };
      this.lessonVisible = true;
    },

    onLessonScore(score) {
      if (score === null || score === undefined) return;
      this.lessonScores = { ...this.lessonScores, [this.lessonLink.id]: score };
    },

    async switchStage(s) {
      this.stage = s;
      this.flat = [];
      this.expanded = {};
      this.selectedId = "";
      if (this.view !== "list") {
        await this.load();
        await this.loadNet();
      } else {
        await this.load();
      }
    },

    async switchSubject(s) {
      this.subject = s;
      this.flat = [];
      this.expanded = {};
      this.selectedId = "";
      if (this.view !== "list") {
        await this.load();
        await this.loadNet();
      } else {
        await this.load();
      }
    },

    toggle(id) {
      this.expanded[id] = !this.expanded[id];
    },

    openView(leaf) {
      const parent = this.flat.find((x) => x.id === leaf.parentId);
      const relatedIds = (leaf.related || "")
        .split(/[,，、;；\s]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      const relatedTitles = relatedIds
        .map((id) => (this.flat.find((x) => x.id === id) || {}).title)
        .filter(Boolean);
      this.detail = {
        ...leaf,
        parentTitle: parent ? parent.title : "",
        relatedTitles,
      };
      this.editing = false;
      this.visible = true;
    },

    openCreate(parent) {
      if (!requireLoginPage(this, "维护知识点")) return;
      this.form = this.emptyForm();
      this.form.subject = this.subject;
      if (parent) {
        this.form.parentId = parent.id;
        this.form.parentTitle = parent.title;
      }
      this.editing = true;
      this.visible = true;
    },

    openEdit(node) {
      this.startEdit(node);
    },

    startEdit(node) {
      if (!this.canEdit(node)) {
        ElMessage.warning("系统内置知识点不可修改，可联系管理员授予「知识体系编辑」权限");
        return;
      }
      const parent = this.flat.find((x) => x.id === node.parentId);
      this.form = {
        id: node.id || "",
        subject: node.subject || this.subject,
        parentId: node.parentId || "",
        parentTitle: parent ? parent.title : "",
        title: node.title || "",
        content: node.content || "",
        standard: node.standard || "",
        keyPoints: node.keyPoints || "",
        difficulties: node.difficulties || "",
        related: (node.related || "")
          .split(/[,，、;；\s]+/)
          .map((x) => x.trim())
          .filter(Boolean),
      };
      this.editing = true;
      this.visible = true;
    },

    close() {
      this.visible = false;
      this.editing = false;
    },

    /** 让 AI 给出「具体内容 / 课标要求 / 重点 / 难点」初稿，老师改完再保存 */
    async generateDraft() {
      if (!this.form.title.trim()) {
        ElMessage.warning("请先填写知识点标题");
        return;
      }
      const filled = ["content", "standard", "keyPoints", "difficulties"].some(
        (k) => (this.form[k] || "").trim()
      );
      if (filled) {
        try {
          await ElMessageBox.confirm("生成的初稿会覆盖当前填写的内容，继续？", "AI 生成初稿", {
            confirmButtonText: "继续",
            cancelButtonText: "取消",
            type: "warning",
          });
        } catch (e) {
          return;
        }
      }
      this.drafting = true;
      try {
        const payload = {
          title: this.form.title.trim(),
          subject: this.form.subject || this.subject,
          stage: this.stage,
          parentTitle: this.form.parentTitle || "",
        };
        const res = await this.$axios({
          url: "knowledge-link/draft",
          method: "POST",
          data: { data: await this.$aesEncrypt(payload) },
        });
        if (res.code === 200 && res.data) {
          this.form.content = res.data.content || "";
          this.form.standard = res.data.standard || "";
          this.form.keyPoints = res.data.keyPoints || "";
          this.form.difficulties = res.data.difficulties || "";
          ElMessage.success("已生成初稿，请按实际教学情况修改后保存");
        } else {
          ElMessage.error(res.msg || "生成失败");
        }
      } catch (e) {
        ElMessage.error("生成失败，请稍后重试");
      }
      this.drafting = false;
    },

    async save() {
      if (!this.form.title.trim()) {
        ElMessage.warning("请填写标题");
        return;
      }
      this.saving = true;
      try {
        const payload = {
          id: this.form.id || null,
          subject: this.form.subject,
          parentId: this.form.parentId,
          title: this.form.title,
          content: this.form.content,
          standard: this.form.standard,
          keyPoints: this.form.keyPoints,
          difficulties: this.form.difficulties,
          related: (this.form.related || []).join(","),
          stage: this.stage,
        };
        const res = await this.$axios({
          url: "knowledge-link/save",
          method: "POST",
          data: { data: await this.$aesEncrypt(payload) },
        });
        if (res.code === 200) {
          ElMessage.success("已保存");
          if (this.form.parentId) {
            this.expanded[this.form.parentId] = true;
          }
          this.close();
          await this.load();
          if (this.view !== "list") await this.loadNet();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请稍后重试");
      }
      this.saving = false;
    },

    async remove(node) {
      if (node.isSystem) {
        ElMessage.warning("系统内置知识点不支持删除，可编辑修正其中的内容");
        return;
      }
      const isRoot = !node.parentId;
      try {
        await ElMessageBox.confirm(
          isRoot
            ? `删除「${node.title}」会连同其下所有知识点一起删除，确定？`
            : `确定删除知识点「${node.title}」？`,
          "删除",
          { confirmButtonText: "删除", cancelButtonText: "取消", type: "warning" }
        );
      } catch (e) {
        return;
      }
      try {
        const res = await this.$axios({
          url: "knowledge-link/remove",
          method: "POST",
          data: { data: await this.$aesEncrypt({ id: node.id }) },
        });
        if (res.code === 200) {
          ElMessage.success("已删除");
          await this.load();
        } else {
          ElMessage.error(res.msg || "删除失败");
        }
      } catch (e) {
        ElMessage.error("删除失败");
      }
    },
  },
};
</script>
